import React from "react";
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  VStack,
  Divider,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";
import FaqSection from "../FaqSection";
import { medicalWebPageSchema, serviceSchema } from "../../seo/schema";
import { absoluteUrl } from "../../seo/siteConfig";

// Rendered by <FaqSection> and emitted as FAQPage JSON-LD by <Seo>.
// Keep the two in sync by only editing this array.
const FAQS = [
  {
    q: "How long do dental crowns and bridges last?",
    a: "On average, dental crowns and bridges can last anywhere from 5 to 15 years, depending on the material used and how well they are cared for. With proper oral hygiene and regular dental visits, they can last even longer.",
  },
  {
    q: "Are dental crowns covered by insurance?",
    a: "Many dental insurance plans cover part of the cost of crowns, especially if they are medically necessary. It's best to check with your insurance provider for specific details.",
  },
  {
    q: "How long does it take to get a dental crown?",
    a: "The process of getting a dental crown typically requires two visits: one to prepare the tooth and take impressions, and a second to place the crown after it’s made. Each visit usually lasts between 30 minutes to an hour.",
  },
  {
    q: "Can I eat with a temporary crown?",
    a: "Yes, you can eat with a temporary crown, but avoid sticky or hard foods that could dislodge it. Be cautious with your chewing on the side with the temporary crown.",
  },
  {
    q: "Can dental crowns be whitened?",
    a: "No, crowns do not respond to whitening treatments. However, they can be replaced if you want to match them to your new smile after whitening your natural teeth.",
  },
  {
    q: "How do I care for my dental crown?",
    a: "Brush and floss your crown regularly to prevent plaque buildup. Avoid chewing on hard objects and consider using a night guard if you grind your teeth.",
  },
  {
    q: "Can a crown fall off?",
    a: "Yes, although rare, crowns can come loose due to wear, decay, or improper bonding. If a crown falls off, contact your dentist immediately for a re-cementing.",
  },
  {
    q: "Is getting a dental crown painful?",
    a: "Getting a crown is typically not painful. Your dentist will use a local anesthetic to numb the area, ensuring you are comfortable during the procedure.",
  },
  {
    q: "Can I get a crown if I have gum disease?",
    a: "It’s important to treat gum disease before getting a crown. If the gum tissue isn’t healthy, the crown may not fit properly, and the tooth could become infected.",
  },
  {
    q: "What happens if I don’t get a crown when needed?",
    a: "If you delay getting a crown for a damaged or weakened tooth, the tooth may continue to decay or break, leading to more severe problems like tooth loss or infection.",
  },
];

const PAGE_PATH = "/bridges-and-crowns";
const PAGE_URL = absoluteUrl(PAGE_PATH);

const BridgesAndCrowns = () => {
  return (
    <Box bg="#fceee5">
      <Seo
        path={PAGE_PATH}
        title="Dental Crowns & Bridges in Jaipur | Tooth Restoration"
        description="Restore damaged or missing teeth with dental crowns and bridges in Vaishali Nagar, Jaipur. Long-lasting, natural-looking work at Qualiteeth Dental Care."
        type="article"
        faqs={FAQS}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Dental Bridges and Crowns", path: PAGE_PATH },
        ]}
        schemas={[
          medicalWebPageSchema({
            url: PAGE_URL,
            title: "Dental Crowns & Bridges in Jaipur | Tooth Restoration",
            description: "Restore damaged or missing teeth with dental crowns and bridges in Vaishali Nagar, Jaipur. Long-lasting, natural-looking work at Qualiteeth Dental Care.",
            procedureName: "Dental Crown and Bridge",
          }),
          serviceSchema({
            url: PAGE_URL,
            name: "Dental Crown and Bridge",
            description: "Restore damaged or missing teeth with dental crowns and bridges in Vaishali Nagar, Jaipur. Long-lasting, natural-looking work at Qualiteeth Dental Care.",
          }),
        ]}
      />
      <Box maxW="4xl" mx="auto" py={8} px={4} marginTop="80px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "5xl" }}
          mb={6}
          textAlign="center"
          fontWeight="light"
        >
          Everything You Need to Know About Dental Crowns and Bridges
        </Heading>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="normal" mb={4}>
          A dental crown is a cap that covers and strengthens a weak, broken or root-canal-treated tooth. A bridge replaces one or more missing teeth by anchoring artificial teeth to the healthy teeth on either side of the gap. Both are made from porcelain, zirconia or metal-ceramic and are fixed permanently in place.
        </Text>
        <Text fontSize="sm" color="gray.600" mb={4} fontStyle="italic">
          By Dr. Aishwarya Sarda, MDS — Conservative Dentistry & Endodontics
        </Text>
        <Divider mb={6} />
        <VStack spacing={4} align="stretch">
          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What Are Dental Crowns?
            </Heading>
            <Text mt={2} fontWeight="light">
              Dental crowns are custom-made caps designed to cover a damaged or
              weakened tooth. They are typically made from durable materials
              such as porcelain, metal, or a combination of both. Crowns restore
              the tooth’s shape, size, strength, and appearance, ensuring that
              it functions like a natural tooth.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              When Is a Dental Crown Needed?
            </Heading>
            <Text mt={2} fontWeight="light">
              Dental crowns are necessary in the following situations:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>To Protect Weakened Teeth:</strong> If a tooth is
                  severely worn down or has a large filling, a crown can prevent
                  it from breaking or decaying further.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>After Root Canal Treatment:</strong> When a tooth has
                  undergone a root canal, it often becomes fragile. A crown
                  protects the tooth and restores its function.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>To Restore a Broken or Chipped Tooth:</strong> If a
                  tooth has cracked or fractured, a crown can restore its
                  appearance and strength.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>To Cover a Dental Implant:</strong> Crowns are
                  commonly used to cover dental implants after tooth loss.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Cosmetic Improvement:</strong> Crowns can be used to
                  cover discolored or misshapen teeth, giving you a more
                  natural, appealing smile.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Types of Crowns
            </Heading>
            <Text mt={2} fontWeight="light">
              There are several types of dental crowns, each made from different
              materials. The choice of crown depends on factors like the
              location of the tooth, its function, and the patient’s
              preferences.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Porcelain Crowns:</strong> These are natural-looking
                  crowns that blend seamlessly with your teeth, making them
                  ideal for front teeth.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Metal Crowns:</strong> Made from metals such as gold,
                  platinum, or other alloys, these crowns are highly durable and
                  resistant to wear. They are often used for back teeth where
                  they are less visible.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Porcelain-Fused-to-Metal Crowns (PFM):</strong> These
                  crowns combine the durability of metal with the aesthetic
                  appeal of porcelain. They are often used for both front and
                  back teeth.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Zirconia Crowns:</strong> Zirconia crowns are
                  incredibly durable and can be customized to match the natural
                  color of your teeth. They are an excellent option for patients
                  who need a crown for both cosmetic and functional reasons.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Resin Crowns:</strong> Made from composite resin,
                  these crowns are more affordable but less durable than other
                  options. They are typically used for short-term solutions or
                  for patients on a budget.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Comparison Between Crowns and Bridges
            </Heading>
            <Text mt={2} fontWeight="light">
              Both crowns and bridges are common dental restorations, but they
              serve different purposes:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Crowns:</strong> A crown is placed on a single damaged
                or weakened tooth to restore its strength and appearance. Crowns
                are ideal when a tooth is too damaged to function properly but
                is still intact.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Better Functionality:</strong> Dentures allow you to
                chew and speak more comfortably, improving your ability to enjoy
                a variety of foods and communicate clearly.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Bridges:</strong> A bridge is used to replace one or
                more missing teeth by attaching artificial teeth to adjacent
                healthy teeth. Bridges fill gaps created by missing teeth,
                helping restore normal chewing and speaking ability.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              How Long Can You Expect a Dental Crown to Last?
            </Heading>
            <Text mt={2} fontWeight="light">
              With proper care, dental crowns can last anywhere from 10 to 15
              years, or even longer. Factors that affect the longevity of your
              crown include oral hygiene habits, the material of the crown, and
              whether you grind your teeth or use your teeth to chew hard
              objects. Regular dental visits are essential for ensuring that
              your crown stays in good condition.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Do's and Don'ts with Dental Crowns
            </Heading>
            <Text mt={2} fontWeight="bold">
              Do’s:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Do maintain good oral hygiene:</strong> Brush your
                crowns twice a day with a soft-bristled brush, floss regularly,
                and use mouthwash to prevent plaque buildup.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Do visit your dentist for regular check-ups:</strong>
                {" Regular dental visits will help monitor the condition of your crowns and ensure they remain in good shape."}
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Do protect your crown if necessary:</strong> If you
                grind your teeth at night, ask your dentist for a night guard to
                prevent damage to your crown.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="bold">
              Don’ts:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Don’t chew on hard objects:</strong> Avoid biting into
                hard foods like ice, hard candies, or nuts, as they can crack or
                damage your crown.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Don’t skip dental appointments:</strong> Regular
                checkups ensure that your crowns fit properly and remain intact.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                <strong>Don’t use your crown as a tool:</strong> Avoid using
                your teeth or crown to open packages or bottles.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Dentures Myths and Facts
            </Heading>
            <Text mt={2} fontWeight="light">
              <strong>Myth 1:</strong> Dental crowns are painful.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> Getting a crown is a routine dental
                procedure that typically causes little to no discomfort. Your
                dentist will numb the area around the tooth, so you shouldn't
                feel pain during the procedure.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 2:</strong> Dental crowns are only for the elderly.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> People of all ages may need dental crowns
                to restore damaged or weakened teeth. Crowns are often used to
                protect teeth after root canal therapy, a common treatment for
                both children and adults.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 3:</strong> Crowns don’t look natural.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> Modern crowns, particularly porcelain and
                zirconia, are designed to mimic the appearance of natural teeth,
                offering a realistic and aesthetically pleasing result.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 4:</strong> Dental crowns require constant
              replacement.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> With proper care, crowns can last many
                years, typically between 10 and 15 years, before needing
                replacement or adjustments.
              </ListItem>
            </List>
            <Text mt={2} fontWeight="light">
              <strong>Myth 5:</strong> Dental crowns are expensive and not worth
              it.
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                <strong>Fact:</strong> While dental crowns can be an investment,
                they provide significant benefits by preserving your tooth and
                improving your oral health. In the long run, crowns can save you
                from more costly and invasive procedures.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <FaqSection items={FAQS} />
        </VStack>
      </Box>
    </Box>
  );
};

export default BridgesAndCrowns;
